import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { site } from "@/data/siteContent";

const inputClass =
  "bg-white/[0.03] border border-white/10 text-cream placeholder:text-cream-muted/60 focus-visible:border-[#D4AF37] focus-visible:ring-0 h-12 rounded-lg";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name || !email || !msg) {
      toast.error("Please share your name, email and message.");
      return;
    }
    toast.success("Message sent. We will respond soon.");
    setName("");
    setEmail("");
    setMsg("");
  };

  const details = [
    { icon: Phone, label: "Phone", value: site.contact.phone },
    { icon: Mail, label: "Email", value: site.contact.email },
    { icon: MapPin, label: "Address", value: site.contact.address },
    { icon: Clock, label: "Hours", value: site.contact.hours },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section relative"
    >
      <div className="container-luxe">
        <div className="reveal max-w-2xl">
          <div className="text-[11px] tracking-[0.4em] uppercase text-gold-light">
            Contact · संपर्क
          </div>
          <h2
            data-testid="contact-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-cream"
          >
            Write to{" "}
            <span className="italic font-editorial gold-text">us</span>.
          </h2>
          <p className="mt-5 text-cream-muted">
            For general enquiries, media requests or partnership opportunities,
            we would love to hear from you.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {/* Info column */}
          <div className="reveal glass-strong rounded-3xl p-8 sm:p-10">
            <div className="grid sm:grid-cols-2 gap-6">
              {details.map((d) => (
                <div
                  key={d.label}
                  data-testid={`contact-detail-${d.label.toLowerCase()}`}
                  className="flex gap-4"
                >
                  <div className="w-11 h-11 shrink-0 rounded-full gold-border grid place-items-center">
                    <d.icon className="w-4 h-4 text-gold" strokeWidth={1.4} />
                  </div>
                  <div>
                    <div className="text-[11px] tracking-[0.28em] uppercase text-cream-muted">
                      {d.label}
                    </div>
                    <div className="mt-1 text-cream text-[14px] break-words">
                      {d.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Google Map placeholder */}
            <div
              data-testid="contact-map"
              className="mt-8 rounded-2xl overflow-hidden aspect-[16/9] gold-border relative"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 40%, rgba(212,175,55,0.15), transparent 55%), radial-gradient(circle at 70% 60%, rgba(30,64,175,0.25), transparent 55%), linear-gradient(135deg, #0a1428, #101d38)",
              }}
            >
              <div className="absolute inset-0 grid place-items-center text-center px-6">
                <div>
                  <MapPinned
                    className="w-10 h-10 mx-auto text-gold"
                    strokeWidth={1.2}
                  />
                  <div className="mt-4 font-display text-[11px] tracking-[0.32em] text-gold">
                    GOOGLE MAP PLACEHOLDER
                  </div>
                  <div className="mt-2 text-[12px] tracking-[0.22em] uppercase text-cream-muted">
                    Embed your location here
                  </div>
                </div>
              </div>
              {/* faux grid lines */}
              <svg
                aria-hidden
                className="absolute inset-0 w-full h-full opacity-15"
              >
                <defs>
                  <pattern
                    id="grid"
                    width="40"
                    height="40"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 40 0 L 0 0 0 40"
                      fill="none"
                      stroke="#D4AF37"
                      strokeWidth="0.5"
                    />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="reveal glass rounded-3xl p-8 sm:p-10 flex flex-col gap-5"
          >
            <div>
              <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                Name
              </Label>
              <Input
                data-testid="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className={`${inputClass} mt-2`}
              />
            </div>
            <div>
              <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                Email
              </Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={`${inputClass} mt-2`}
              />
            </div>
            <div className="flex-1">
              <Label className="text-cream text-[11px] tracking-[0.22em] uppercase">
                Message
              </Label>
              <Textarea
                data-testid="contact-message"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Type your message..."
                rows={6}
                className={`${inputClass} mt-2 h-auto py-3 resize-none`}
              />
            </div>
            <Button
              type="submit"
              data-testid="contact-submit"
              className="rounded-full h-12 bg-[#D4AF37] text-[#050B14] hover:bg-[#F3E5AB] font-semibold text-[12px] tracking-[0.24em] uppercase"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
